import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, X } from "lucide-react";

// Reusable confirmation dialog — imperative `confirm()` API + a single
// <ConfirmHost /> mounted at the workspace root. Callers don't manage
// open state or wire JSX themselves; they just `await confirm({...})`.
//
//     const ok = await confirm({
//         title: "Delete record?",
//         message: "This can't be undone.",
//         variant: "danger",
//         confirmLabel: "Delete",
//     });
//     if (!ok) return;
//
// Returns `true` when confirmed, `false` when cancelled / dismissed.

export type ConfirmVariant = "default" | "danger";

export interface ConfirmOptions {
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: ConfirmVariant;
}

interface PendingState extends ConfirmOptions {
    resolve: (value: boolean) => void;
}

// Tiny singleton store — pub/sub so `confirm()` works from anywhere without
// React context plumbing. Only one dialog can be pending at a time; a second
// call while one is open resolves the first as cancelled before opening the
// new one (cheap protection against unintentional stacking).
let pending: PendingState | null = null;
const listeners = new Set<(state: PendingState | null) => void>();

function emit(): void {
    for (const fn of listeners) fn(pending);
}

export function confirm(opts: ConfirmOptions): Promise<boolean> {
    if (pending) {
        pending.resolve(false);
        pending = null;
    }
    return new Promise<boolean>((resolve) => {
        pending = { ...opts, resolve };
        emit();
    });
}

function resolveAndClose(value: boolean): void {
    if (!pending) return;
    const { resolve } = pending;
    pending = null;
    emit();
    resolve(value);
}

// Mount once near the root. Subscribes to the singleton store and renders
// the actual Radix Dialog whenever a confirm() call is pending.
export function ConfirmHost() {
    const [state, setState] = useState<PendingState | null>(pending);

    useEffect(() => {
        listeners.add(setState);
        return () => {
            listeners.delete(setState);
        };
    }, []);

    const open = state !== null;
    const variant = state?.variant ?? "default";
    const isDanger = variant === "danger";

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(next) => {
                if (!next) resolveAndClose(false);
            }}
        >
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" />
                <Dialog.Content className={`confirm-dialog ${isDanger ? "confirm-dialog--danger" : ""}`}>
                    <header className="confirm-dialog__head">
                        <span className={`confirm-dialog__icon ${isDanger ? "is-danger" : ""}`}>
                            <AlertTriangle size={16} />
                        </span>
                        <Dialog.Title className="confirm-dialog__title">
                            {state?.title ?? ""}
                        </Dialog.Title>
                        <Dialog.Close className="theme-dialog__close" aria-label="Close">
                            <X size={16} />
                        </Dialog.Close>
                    </header>
                    {state?.message ? (
                        <Dialog.Description className="confirm-dialog__message">
                            {state.message}
                        </Dialog.Description>
                    ) : (
                        // Radix requires a Description for a11y unless explicitly omitted —
                        // empty visually-hidden node keeps the API clean for title-only calls.
                        <Dialog.Description className="theme-dialog__sr-only">
                            {state?.title ?? ""}
                        </Dialog.Description>
                    )}
                    <footer className="confirm-dialog__foot">
                        {/* An explicit empty `cancelLabel` ("") means the caller
                            wants an acknowledge-only dialog (e.g. "Could not save
                            … OK") — render no cancel button. `undefined` still
                            falls back to the default "Cancel". */}
                        {state?.cancelLabel !== "" ? (
                            <button
                                type="button"
                                className="btn confirm-dialog__cancel"
                                onClick={() => resolveAndClose(false)}
                                data-viewid="ede-dialog-cancel"
                            >
                                {state?.cancelLabel ?? "Cancel"}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className={`btn confirm-dialog__confirm ${isDanger ? "btn--negative" : "btn--primary"}`}
                            onClick={() => resolveAndClose(true)}
                            autoFocus
                            data-viewid="ede-dialog-confirm"
                        >
                            {state?.confirmLabel ?? "Confirm"}
                        </button>
                    </footer>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
